// 1Rotary — Extended sample data for enhanced modules
// Branch Performance · Inventory Intelligence · Financial · Merchandising · Workbench

import { BRANCHES, SKUS, SUPPLIERS, CATEGORIES, MONTHLY_REVENUE, KPI_SNAPSHOT } from "./data";

// Deterministic pseudo-random
let _seed = 9_142_026;
function rnd() { _seed = (_seed * 1664525 + 1013904223) % 4294967296; return _seed / 4294967296; }
function r(min, max) { return min + rnd() * (max - min); }
function ri(min, max) { return Math.floor(r(min, max + 1)); }
function pick(arr) { return arr[Math.floor(rnd() * arr.length)]; }

/* ============================================================
   BRANCH PERFORMANCE — extended productivity & health
============================================================ */
export const BRANCH_PRODUCTIVITY = BRANCHES.map(b => {
  const sqm = ri(60, 280);
  const footfall = ri(380, 4200);
  const txns = Math.round(footfall * r(0.18, 0.46));
  const basket = Math.round(b.monthlyActual / Math.max(txns, 1));
  const convRate = txns / footfall;
  const revPerSqm = Math.round(b.monthlyActual / sqm);
  const revPerHead = Math.round(b.monthlyActual / b.headcount);
  // Same-store sales growth — YoY
  const sssg = r(-0.18, 0.34);
  // Health score components (each 0-100)
  const revScore = Math.min(100, Math.round(b.achievement * 100));
  const stockScore = Math.round((1 - b.stockoutRate / 0.10) * 100);
  const productivityScore = Math.round(Math.min(revPerSqm / 36000, 1) * 100);
  const accuracyScore = Math.round(b.inventoryAccuracy * 100);
  const npsScore = ri(38, 88);
  const healthScore = Math.round(
    revScore * 0.32 + stockScore * 0.22 + productivityScore * 0.18 + accuracyScore * 0.14 + npsScore * 0.14
  );
  return {
    branchId: b.id,
    name: b.name,
    region: b.region,
    brand: b.brand,
    sqm,
    footfall,
    transactions: txns,
    basket,
    convRate,
    revPerSqm,
    revPerHead,
    sssg,
    healthScore,
    components: { rev: revScore, stock: stockScore, prod: productivityScore, acc: accuracyScore, nps: npsScore },
  };
});

// Same-Store Sales Growth (SSSG) by region over last 12 weeks
export const SSSG_TREND = (() => {
  const weeks = Array.from({ length: 12 }, (_, i) => `W${i + 1}`);
  const regions = ["Metro Manila", "South Luzon", "North Luzon", "Visayas", "Mindanao"];
  return weeks.map((w, wi) => {
    const row = { week: w };
    regions.forEach(reg => {
      const trend = (wi - 6) * 0.012 + r(-0.04, 0.05);
      const regBias = { "Metro Manila": 0.02, "South Luzon": 0.04, "North Luzon": -0.01, "Visayas": 0.06, "Mindanao": 0.09 }[reg];
      row[reg] = +(trend + regBias).toFixed(3);
    });
    return row;
  });
})();

// Funnel — network rollup
export const NETWORK_FUNNEL = (() => {
  const totalFoot = BRANCH_PRODUCTIVITY.reduce((s, x) => s + x.footfall, 0);
  const totalTxn = BRANCH_PRODUCTIVITY.reduce((s, x) => s + x.transactions, 0);
  const totalRev = BRANCHES.reduce((s, b) => s + b.monthlyActual, 0);
  const avgBasket = Math.round(totalRev / Math.max(totalTxn, 1));
  return [
    { stage: "Footfall", value: totalFoot, label: `${Math.round(totalFoot / 1000)}K visits/mo` },
    { stage: "Engaged", value: Math.round(totalFoot * 0.62), label: "Walked sales-floor" },
    { stage: "Quoted", value: Math.round(totalFoot * 0.41), label: "Received quote / consultation" },
    { stage: "Transactions", value: totalTxn, label: `${Math.round((totalTxn / totalFoot) * 100)}% conversion` },
    { stage: "Repeat (30d)", value: Math.round(totalTxn * 0.27), label: `Avg basket ${avgBasket.toLocaleString("en-PH")}` },
  ];
})();

// Peer benchmark — top vs cluster avg vs at-risk
export const PEER_BENCHMARK = (() => {
  const sorted = [...BRANCH_PRODUCTIVITY].sort((a, b) => b.healthScore - a.healthScore);
  const top = sorted[0];
  const atRisk = sorted[sorted.length - 1];
  const avg = (k) => Math.round(BRANCH_PRODUCTIVITY.reduce((s, x) => s + x[k], 0) / BRANCH_PRODUCTIVITY.length);
  const radar = ["rev", "stock", "prod", "acc", "nps"].map(c => ({
    axis: ({ rev: "Revenue", stock: "Stock Health", prod: "Productivity", acc: "Accuracy", nps: "NPS" })[c],
    Top: top.components[c],
    Average: Math.round(BRANCH_PRODUCTIVITY.reduce((s, x) => s + x.components[c], 0) / BRANCH_PRODUCTIVITY.length),
    AtRisk: atRisk.components[c],
  }));
  return { top, atRisk, avg: { sqm: avg("sqm"), footfall: avg("footfall"), basket: avg("basket"), revPerSqm: avg("revPerSqm") }, radar };
})();

/* ============================================================
   INVENTORY INTELLIGENCE — extended
============================================================ */

// ABC × XYZ classification (X=stable, Y=variable, Z=volatile)
// Realistic bias: A-class fast-movers tend to have more stable demand (lower CV);
// C-class slow-movers are noisier. This ensures the AX corner is non-empty
// (auto-replen sweet spot) and CZ carries the phase-out tail.
export const SKU_ABCXYZ = SKUS.map(s => {
  const cvRange = s.abc === "A" ? [0.05, 0.55]     // A-class: stable to variable, rarely volatile
                : s.abc === "B" ? [0.15, 0.85]     // B-class: variable to volatile
                :                  [0.35, 1.4];    // C-class: mostly volatile
  const cv = r(cvRange[0], cvRange[1]);
  const xyz = cv < 0.25 ? "X" : cv < 0.6 ? "Y" : "Z";
  return { skuId: s.id, name: s.name, category: s.category, abc: s.abc, xyz, cv: +cv.toFixed(2), value: s.monthlyUnits * s.unitCost };
});

export const ABCXYZ_MATRIX = (() => {
  const cells = {};
  ["A", "B", "C"].forEach(a => ["X", "Y", "Z"].forEach(x => { cells[`${a}${x}`] = { code: `${a}${x}`, count: 0, value: 0 }; }));
  SKU_ABCXYZ.forEach(s => {
    const k = `${s.abc}${s.xyz}`;
    cells[k].count += 1;
    cells[k].value += s.value;
  });
  // Tag with policy
  const policy = {
    AX: "Tight SS · auto-reorder", AY: "Buffer SS · weekly review", AZ: "Air-freight on signal · high SS",
    BX: "Standard ROP", BY: "Forecast monthly", BZ: "Make-to-order option",
    CX: "Bulk infrequent", CY: "Bin replenish", CZ: "Phase-out candidate",
  };
  return Object.values(cells).map(c => ({ ...c, policy: policy[c.code] }));
})();

// ---------------------------------------------------------------------------
// ABC × XYZ Policy Compliance — 3 sub-metrics per cell + aggregate score.
//   auto_replen : % of SKUs on ML-001 Auto-Replenishment
//   safety_stock: % of SKUs with safety-stock at or above ABC-XYZ recommendation
//   moq_discipline: % of POs matching MOQ-adjusted ML suggestion (last 90d)
// Realistic bias: AX = near-perfect discipline; CZ = execution gap (why it's
// still hanging around); mid-cells = 60-80%.
// ---------------------------------------------------------------------------
const COMPLIANCE_MAP = {
  AX: { auto_replen: 0.96, safety_stock: 0.94, moq_discipline: 0.92 },
  AY: { auto_replen: 0.82, safety_stock: 0.88, moq_discipline: 0.79 },
  AZ: { auto_replen: 0.34, safety_stock: 0.68, moq_discipline: 0.58 },
  BX: { auto_replen: 0.72, safety_stock: 0.78, moq_discipline: 0.80 },
  BY: { auto_replen: 0.62, safety_stock: 0.70, moq_discipline: 0.66 },
  BZ: { auto_replen: 0.28, safety_stock: 0.52, moq_discipline: 0.48 },
  CX: { auto_replen: 0.55, safety_stock: 0.60, moq_discipline: 0.72 },
  CY: { auto_replen: 0.24, safety_stock: 0.42, moq_discipline: 0.44 },
  CZ: { auto_replen: 0.12, safety_stock: 0.28, moq_discipline: 0.22 },
};
export const ABCXYZ_COMPLIANCE = Object.fromEntries(
  Object.entries(COMPLIANCE_MAP).map(([code, m]) => {
    const overall = +((m.auto_replen + m.safety_stock + m.moq_discipline) / 3).toFixed(3);
    const status = overall >= 0.85 ? "success" : overall >= 0.65 ? "warning" : "critical";
    return [code, { ...m, overall, status }];
  })
);

// DOH histogram across branches+SKUs (60 buckets, 0..120 days)
export const DOH_HISTOGRAM = (() => {
  const buckets = Array.from({ length: 13 }, (_, i) => ({ range: `${i * 10}-${(i + 1) * 10}`, mid: i * 10 + 5, count: 0 }));
  for (let i = 0; i < 1400; i++) {
    const v = Math.max(0, Math.min(129, r(0, 130) * (rnd() < 0.5 ? 0.6 : 1.4)));
    const idx = Math.min(12, Math.floor(v / 10));
    buckets[idx].count += 1;
  }
  return buckets;
})();

// 90-day stockout heat calendar (90 days × top 12 SKUs)
export const STOCKOUT_CALENDAR = (() => {
  const skus = SKUS.filter(s => s.abc === "A").slice(0, 10);
  return skus.map(s => {
    const days = Array.from({ length: 90 }, (_, d) => {
      const p = rnd();
      const heat = p < 0.04 ? 3 : p < 0.10 ? 2 : p < 0.22 ? 1 : 0; // 0 healthy → 3 critical
      return heat;
    });
    return { skuId: s.id, name: s.name, abc: s.abc, days };
  });
})();

// Dead-stock detector (≥120 days idle)
export const DEAD_STOCK = (() => {
  const rows = [];
  const slow = [...SKUS].sort((a, b) => a.velocityIndex - b.velocityIndex).slice(0, 14);
  slow.forEach(s => {
    for (let i = 0; i < 2; i++) {
      const branch = pick(BRANCHES);
      const days = ri(120, 380);
      const units = ri(8, 240);
      rows.push({
        skuId: s.id, sku: s.name, branchId: branch.id, branch: branch.name, region: branch.region,
        daysIdle: days, units, value: units * s.unitCost, recommendation: days > 240 ? "Liquidate (Shopee promo)" : "Transfer to high-velocity branch",
      });
    }
  });
  return rows.sort((a, b) => b.value - a.value).slice(0, 18);
})();

// Auto-replenishment AI suggestions
export const REPLEN_QUEUE = (() => {
  const fast = [...SKUS].sort((a, b) => b.velocityIndex - a.velocityIndex).slice(0, 18);
  return fast.map((s, i) => {
    const branch = pick(BRANCHES);
    const cur = ri(2, 28);
    const rop = ri(35, 90);
    const eoq = ri(80, 360);
    const conf = +(r(0.78, 0.97)).toFixed(2);
    return {
      id: `REP-${100 + i}`,
      sku: s.name, skuId: s.id, branch: branch.name, branchId: branch.id, region: branch.region,
      currentUnits: cur, reorderPoint: rop, suggestedQty: eoq,
      leadTime: s.leadTimeDays, value: eoq * s.unitCost,
      confidence: conf,
      reason: cur < rop * 0.4
        ? "Stockout in 4–7 days at current velocity"
        : "Below ROP with rising 28-day demand",
      urgency: cur < rop * 0.4 ? "critical" : cur < rop * 0.7 ? "warning" : "info",
    };
  });
})();

// Inventory turnover by category — last 12 months
export const TURNOVER_TREND = (() => {
  const months = MONTHLY_REVENUE.map(m => m.month);
  return months.map((m, i) => ({
    month: m,
    Auto: +(r(7.8, 9.6) + Math.sin(i / 2) * 0.4).toFixed(2),
    HVAC: +(r(9.5, 11.8) + Math.sin(i / 2 + 1) * 0.5).toFixed(2),
    Refrig: +(r(7.2, 8.8) + Math.sin(i / 2 + 2) * 0.3).toFixed(2),
    Consumables: +(r(11.4, 13.2) + Math.sin(i / 2 + 3) * 0.4).toFixed(2),
  }));
})();

/* ============================================================
   FINANCIAL — 13-week cashflow, lead time, WC, AR aging, GM walk
============================================================ */
export const CASHFLOW_13W = (() => {
  let opening = 86_400_000; // ₱86.4M opening cash
  const minThreshold = 45_000_000;
  const rows = [];
  for (let w = 1; w <= 13; w++) {
    // AR collections — seasonal
    const arBase = 38_000_000 * r(0.84, 1.14);
    const ar = Math.round(arBase * (1 + Math.sin(w / 3) * 0.08));
    // AP disbursements (suppliers + LC settlements)
    const ap = Math.round(28_500_000 * r(0.82, 1.22) + (w === 4 || w === 9 ? 18_000_000 : 0));
    // Payroll (bi-weekly humps)
    const payroll = w % 2 === 0 ? 9_200_000 : 1_200_000;
    // OpEx
    const opex = Math.round(6_400_000 * r(0.9, 1.15));
    // CapEx — only weeks 6, 11
    const capex = w === 6 ? 8_500_000 : w === 11 ? 4_200_000 : 0;
    const netFlow = ar - ap - payroll - opex - capex;
    const closing = opening + netFlow;
    const date = new Date();
    date.setDate(date.getDate() + (w - 1) * 7);
    rows.push({
      week: `W${w}`,
      weekLabel: `${date.toISOString().slice(5, 10)}`,
      opening, ar, ap, payroll, opex, capex,
      netFlow, closing, minThreshold,
      risk: closing < minThreshold ? "critical" : closing < minThreshold * 1.15 ? "warning" : "ok",
    });
    opening = closing;
  }
  return rows;
})();

// Purchasing Lead Time Analysis — per supplier with PO history
export const LEAD_TIME_ANALYSIS = SUPPLIERS.map(s => {
  const observations = Array.from({ length: 14 }, (_, i) => {
    const planned = s.leadTime;
    const variance = r(-0.12, 0.42); // skew toward delays
    const actual = Math.round(planned * (1 + variance));
    return {
      po: `PO-${10000 + ri(0, 9999)}`,
      planned, actual,
      delayDays: actual - planned,
      week: `W${i + 1}`,
    };
  });
  const avgActual = observations.reduce((s2, o) => s2 + o.actual, 0) / observations.length;
  const avgDelay = avgActual - s.leadTime;
  // Attribution breakdown (sums to total avg delay)
  const portCong = Math.max(0, avgDelay * 0.42);
  const supplierCap = Math.max(0, avgDelay * 0.28);
  const customs = Math.max(0, avgDelay * 0.18);
  const lastMile = Math.max(0, avgDelay * 0.12);
  return {
    supplierId: s.id, supplier: s.name, country: s.country, category: s.category,
    plannedLT: s.leadTime, avgActualLT: +avgActual.toFixed(1),
    otd: s.otd, openPOs: s.openPOs, totalValue: s.totalValue,
    rating: s.rating,
    avgDelay: +avgDelay.toFixed(1),
    observations,
    attribution: [
      { factor: "Port Congestion", days: +portCong.toFixed(1) },
      { factor: "Supplier Capacity", days: +supplierCap.toFixed(1) },
      { factor: "Customs Clearance", days: +customs.toFixed(1) },
      { factor: "Last-Mile Local", days: +lastMile.toFixed(1) },
    ],
  };
}).sort((a, b) => b.avgDelay - a.avgDelay);

// Working Capital Scorecard
export const WC_SCORECARD = {
  dio: 38.7, dioTarget: 35.0, dioStatus: "warning",
  dso: 42.1, dsoTarget: 35.0, dsoStatus: "warning",
  dpo: 51.4, dpoTarget: 60.0, dpoStatus: "warning",
  ccc: 38.7 + 42.1 - 51.4, // 29.4 days
  cccTarget: 22.0,
  trend: Array.from({ length: 12 }, (_, i) => ({
    month: MONTHLY_REVENUE[i].month,
    DIO: +(38 + Math.sin(i / 2) * 4 + r(-1, 1)).toFixed(1),
    DSO: +(40 + Math.cos(i / 3) * 3 + r(-1, 1)).toFixed(1),
    DPO: +(52 + Math.sin(i / 4) * 5 + r(-1, 1)).toFixed(1),
  })),
};

// AR Aging buckets
export const AR_AGING = (() => {
  const buckets = [
    { bucket: "0-30 days", amount: 184_200_000, count: 482, status: "success" },
    { bucket: "31-60 days", amount: 62_800_000, count: 168, status: "info" },
    { bucket: "61-90 days", amount: 24_400_000, count: 71, status: "warning" },
    { bucket: "91+ days", amount: 11_900_000, count: 38, status: "critical" },
  ];
  const topRisk = [
    { customer: "Metro Aircon Services Corp", amount: 4_800_000, days: 142, branch: "Pasig Ortigas" },
    { customer: "FastCool Auto Detailing", amount: 3_240_000, days: 118, branch: "Quezon City Cubao" },
    { customer: "Visayas Cold Storage Inc", amount: 2_950_000, days: 96, branch: "Mandaue Cebu" },
    { customer: "Subic Logistics Park", amount: 2_180_000, days: 89, branch: "Subic" },
    { customer: "Davao Fleet Services", amount: 1_840_000, days: 78, branch: "Davao City" },
  ];
  return { buckets, topRisk };
})();

// Gross Margin Walk (current vs prior period)
export const GM_WALK = [
  { label: "Prior Period GM%", value: 33.4, kind: "anchor" },
  { label: "FX Headwind", value: -0.9, kind: "negative" },
  { label: "Freight & Duties", value: -0.6, kind: "negative" },
  { label: "Category Mix (Cons↑)", value: +0.7, kind: "positive" },
  { label: "Shopee Promo Discount", value: -1.4, kind: "negative" },
  { label: "Direct Channel Mix", value: +0.4, kind: "positive" },
  { label: "Supplier Renegotiation", value: +0.2, kind: "positive" },
  { label: "Current GM%", value: 31.8, kind: "anchor" },
];

/* ============================================================
   MERCHANDISING & DEMAND PLANNING — full module
============================================================ */

// Weekly demand plan vs actual (top 12 A-class SKUs × last 12 weeks)
export const DEMAND_PLAN_VS_ACTUAL = (() => {
  const skus = SKUS.filter(s => s.abc === "A").slice(0, 14);
  return skus.map(s => {
    const weeks = Array.from({ length: 12 }, (_, i) => {
      const plan = Math.round(s.monthlyUnits / 4 * r(0.85, 1.15));
      const actual = Math.round(plan * r(0.6, 1.35));
      return { week: `W${i + 1}`, plan, actual, variance: actual - plan, varPct: (actual - plan) / plan };
    });
    const totalPlan = weeks.reduce((s2, w) => s2 + w.plan, 0);
    const totalActual = weeks.reduce((s2, w) => s2 + w.actual, 0);
    const acc = 1 - Math.abs(totalActual - totalPlan) / totalPlan;
    return { skuId: s.id, name: s.name, category: s.category, weeks, accuracy: acc, planSum: totalPlan, actualSum: totalActual };
  });
})();

// SKU Lifecycle stages
export const SKU_LIFECYCLE = (() => {
  const stages = ["Intro", "Growth", "Mature", "Decline"];
  const dist = SKUS.slice(0, 60).map(s => {
    let stage;
    if (s.velocityIndex < 0.3) stage = "Decline";
    else if (s.velocityIndex < 0.55) stage = "Mature";
    else if (s.velocityIndex < 0.78) stage = "Growth";
    else stage = "Intro";
    if (rnd() < 0.18) stage = pick(stages);
    return { skuId: s.id, name: s.name, category: s.category, stage, monthlyUnits: s.monthlyUnits, margin: r(0.18, 0.42) };
  });
  const counts = stages.map(st => ({
    stage: st,
    count: dist.filter(x => x.stage === st).length,
    units: dist.filter(x => x.stage === st).reduce((s, x) => s + x.monthlyUnits, 0),
    color: ({ Intro: "var(--info)", Growth: "var(--success)", Mature: "var(--primary)", Decline: "var(--warning)" })[st],
  }));
  return { dist, counts };
})();

// Price Elasticity matrix
export const PRICE_ELASTICITY = (() => {
  const cats = CATEGORIES.map(c => c.id);
  return cats.map(cat => {
    const elast = +(r(-2.4, -0.4)).toFixed(2);
    const optimalAdj = +(r(-0.06, 0.12)).toFixed(3);
    return {
      category: CATEGORIES.find(c => c.id === cat).name,
      categoryId: cat,
      elasticity: elast,
      bucket: elast < -1.5 ? "Elastic (price-sensitive)" : elast < -0.8 ? "Unit elastic" : "Inelastic",
      optimalAdjPct: optimalAdj,
      revenueImpact: Math.round(KPI_SNAPSHOT.totalRevenueMTD * 0.22 * optimalAdj * Math.abs(elast)),
    };
  });
})();

// Promotion Calendar — next 8 weeks
export const PROMO_CALENDAR = (() => {
  const promos = [
    { name: "Shopee 4.4 Mid-Year Sale", channel: "Shopee", week: "W1", discount: 0.20, skus: 184, expectedUplift: 1.85, cannib: 0.18, status: "scheduled" },
    { name: "Labor Day Cooling Promo", channel: "Physical", week: "W2", discount: 0.10, skus: 96, expectedUplift: 1.32, cannib: 0.08, status: "scheduled" },
    { name: "Auto-AC Service Bundle", channel: "Physical+Direct", week: "W3", discount: 0.15, skus: 42, expectedUplift: 1.48, cannib: 0.12, status: "draft" },
    { name: "Shopee Mega Brand Day", channel: "Shopee", week: "W4", discount: 0.28, skus: 240, expectedUplift: 2.20, cannib: 0.24, status: "scheduled" },
    { name: "Refrigeration Refresh", channel: "Direct", week: "W5", discount: 0.08, skus: 38, expectedUplift: 1.24, cannib: 0.05, status: "draft" },
    { name: "Pre-Summer Stock-Up", channel: "Physical", week: "W6", discount: 0.12, skus: 128, expectedUplift: 1.42, cannib: 0.14, status: "draft" },
    { name: "Shopee 5.5 Anchor Event", channel: "Shopee", week: "W7", discount: 0.22, skus: 220, expectedUplift: 1.92, cannib: 0.20, status: "draft" },
    { name: "Mid-Year Tradesperson Bonus", channel: "Direct", week: "W8", discount: 0.06, skus: 64, expectedUplift: 1.18, cannib: 0.04, status: "draft" },
  ];
  return promos;
})();

// Seasonality + weather correlation (12 months of indices)
export const SEASONALITY_WEATHER = (() => {
  const months = MONTHLY_REVENUE.map(m => m.month);
  const heatIndex = [28, 31, 36, 39, 41, 37, 32, 31, 31, 32, 30, 29];
  const typhoons = [0, 0, 1, 0, 0, 1, 2, 3, 3, 2, 1, 0];
  return months.map((m, i) => ({
    month: m,
    heatIndex: heatIndex[i],
    typhoonCount: typhoons[i],
    autoDemand: +(0.85 + (heatIndex[i] - 28) * 0.04 + r(-0.05, 0.05)).toFixed(2),
    hvacDemand: +(0.80 + (heatIndex[i] - 28) * 0.06 + r(-0.05, 0.05)).toFixed(2),
    refrigDemand: +(0.92 + typhoons[i] * 0.04 + r(-0.05, 0.05)).toFixed(2),
  }));
})();

// Brand × Category gross margin contribution
export const BRAND_CATEGORY_MARGIN = (() => {
  const brands = ["1Rotary", "ROTEK", "North ROTEK"];
  const cats = CATEGORIES;
  const rows = [];
  brands.forEach(b => {
    cats.forEach(c => {
      const rev = ri(8_400_000, 32_800_000);
      const gm = +(r(0.22, 0.42)).toFixed(3);
      rows.push({ brand: b, category: c.name, categoryId: c.id, revenue: rev, gm, margin: Math.round(rev * gm) });
    });
  });
  return rows;
})();

// NPI tracker — New Product Introductions
export const NPI_TRACKER = [
  { id: "NPI-001", sku: "Inverter Compressor 2.0HP — Daikin Series", stage: "Pilot", branches: 8, weeks: 6, units: 124, target: 480, gm: 0.31, status: "on-track" },
  { id: "NPI-002", sku: "EcoCool R290 Refrigerant 6kg", stage: "Scale", branches: 32, weeks: 14, units: 1_840, target: 2_400, gm: 0.38, status: "on-track" },
  { id: "NPI-003", sku: "Smart Thermostat WiFi-Enabled", stage: "Pilot", branches: 5, weeks: 4, units: 86, target: 200, gm: 0.42, status: "at-risk" },
  { id: "NPI-004", sku: "Honda CR-V (2024) AC Assembly", stage: "Sourcing", branches: 0, weeks: 0, units: 0, target: 600, gm: 0.28, status: "blocked" },
  { id: "NPI-005", sku: "Universal Mounting Bracket Kit v2", stage: "Scale", branches: 48, weeks: 22, units: 4_280, target: 4_000, gm: 0.46, status: "outperforming" },
  { id: "NPI-006", sku: "Sanden Hybrid Compressor (EV-ready)", stage: "Brief", branches: 0, weeks: 0, units: 0, target: 0, gm: 0, status: "concept" },
  { id: "NPI-007", sku: "Copper Tubing 5/16\" (premium grade)", stage: "Pilot", branches: 12, weeks: 8, units: 318, target: 600, gm: 0.34, status: "on-track" },
  { id: "NPI-008", sku: "Anti-Bacterial Filter Roll Premium", stage: "Scale", branches: 56, weeks: 18, units: 6_240, target: 5_500, gm: 0.41, status: "outperforming" },
];

/* ============================================================
   WORKBENCH — additional purchasing/lead-time scenarios
============================================================ */
export const PROCUREMENT_SCENARIOS = [
  {
    id: "leadtime-shock",
    name: "Supplier Lead-Time Shock",
    icon: "Clock",
    short: "Model the stockout & revenue impact of additional supplier lead-time delays",
    inputs: [
      { id: "delayDays", label: "Additional Delay (days)", min: 5, max: 60, default: 21, unit: "d" },
      { id: "supplierShare", label: "Affected Supplier Share (%)", min: 5, max: 60, default: 28, unit: "%" },
    ],
  },
  {
    id: "supplier-diversification",
    name: "Single vs Multi-Supplier",
    icon: "GitFork",
    short: "Compare risk concentration of single-sourcing vs multi-supplier diversification",
    inputs: [
      { id: "secondShare", label: "Secondary Supplier Share (%)", min: 0, max: 50, default: 25, unit: "%" },
      { id: "premium", label: "Secondary Cost Premium (%)", min: 0, max: 20, default: 7, unit: "%" },
    ],
  },
  {
    id: "safety-stock",
    name: "Safety Stock Optimizer",
    icon: "Shield",
    short: "Trade-off carrying cost vs service level (fill-rate)",
    inputs: [
      { id: "serviceLevel", label: "Target Service Level (%)", min: 85, max: 99, default: 95, unit: "%" },
      { id: "leadTimeVar", label: "Lead-Time Variability (σ days)", min: 1, max: 18, default: 7, unit: "d" },
    ],
  },
  {
    id: "po-frequency",
    name: "PO Frequency vs Holding Cost",
    icon: "Repeat",
    short: "Optimize order frequency against holding & ordering costs (EOQ)",
    inputs: [
      { id: "ordersPerYear", label: "Orders / Year per A-SKU", min: 4, max: 52, default: 12, unit: "" },
      { id: "holdingPct", label: "Holding Cost (% of inventory value)", min: 8, max: 32, default: 18, unit: "%" },
    ],
  },
];

export function runProcurementSimulation(scenarioId, inputs) {
  switch (scenarioId) {
    case "leadtime-shock": {
      const d = inputs.delayDays;
      const sh = inputs.supplierShare / 100;
      const lostUnits = Math.round(108_000 * sh * (d / 30));
      const lostRev = lostUnits * 1_420;
      const stockoutBranches = Math.min(86, ri(20, 84) * (d / 30));
      return {
        outcomes: [
          { label: "Stockout Units", value: lostUnits, format: "num" },
          { label: "Revenue at Risk", value: lostRev, format: "peso" },
          { label: "Branches Impacted", value: Math.round(stockoutBranches), format: "num" },
          { label: "Air-Freight Premium Required", value: 0.14 + sh * 0.22, format: "pct" },
        ],
        series: Array.from({ length: Math.ceil(d / 7) }).map((_, i) => ({
          week: `W${i + 1}`,
          stockout: Math.round((lostUnits / Math.ceil(d / 7)) * (i + 1)),
          baselineDemand: Math.round(108_000 / 4),
        })),
        narrative: `A ${d}-day lead-time shock on suppliers holding ${(sh * 100).toFixed(0)}% share triggers ~${lostUnits.toLocaleString()} stockout units across ${Math.round(stockoutBranches)} branches. Recommended: air-freight A-class SKUs at +${((0.14 + sh * 0.22) * 100).toFixed(0)}% premium and trigger Capella/GMCC backup suppliers.`,
      };
    }
    case "supplier-diversification": {
      const second = inputs.secondShare / 100;
      const premium = inputs.premium / 100;
      const riskReduction = 0.62 * second;
      const costIncrease = second * premium;
      const wcImpact = Math.round(KPI_SNAPSHOT.inventoryValue * second * 0.04);
      return {
        outcomes: [
          { label: "Single-Source Risk Reduction", value: riskReduction, format: "pct" },
          { label: "Annualized Cost Premium", value: costIncrease, format: "pct" },
          { label: "Working Capital Impact", value: wcImpact, format: "peso" },
          { label: "Recommended Action", value: second > 0.2 ? "Approve" : "Hold", format: "text" },
        ],
        series: [
          { stage: "Single-Source", risk: 100, cost: 100 },
          { stage: "Dual-Source", risk: Math.round(100 - riskReduction * 100), cost: Math.round(100 + costIncrease * 100) },
        ],
        narrative: `Onboarding a secondary supplier at ${(second * 100).toFixed(0)}% share with ${(premium * 100).toFixed(0)}% cost premium reduces single-source risk by ${(riskReduction * 100).toFixed(0)}% — annualized OPEX impact ${(costIncrease * 100).toFixed(2)}%. Worth it for A-class compressors and refrigerants.`,
      };
    }
    case "safety-stock": {
      const sl = inputs.serviceLevel;
      const sigma = inputs.leadTimeVar;
      // z-score lookup (rough)
      const z = sl >= 99 ? 2.33 : sl >= 97 ? 1.88 : sl >= 95 ? 1.65 : sl >= 90 ? 1.28 : 1.04;
      const avgDailyDemand = 6_400;
      const safetyStockUnits = Math.round(z * sigma * Math.sqrt(45) * (avgDailyDemand / 100));
      const investment = Math.round(safetyStockUnits * 1_240);
      const fillRate = sl / 100;
      const stockoutDays = Math.max(0, (1 - fillRate) * 365);
      return {
        outcomes: [
          { label: "Safety Stock (units, A-class)", value: safetyStockUnits, format: "num" },
          { label: "Incremental Inventory Investment", value: investment, format: "peso" },
          { label: "Fill Rate Target", value: fillRate, format: "pct" },
          { label: "Stockout Days / Yr (expected)", value: `${stockoutDays.toFixed(0)} days`, format: "text" },
        ],
        series: Array.from({ length: 7 }).map((_, i) => {
          const slx = 85 + i * 2.5;
          const zx = slx >= 99 ? 2.33 : slx >= 97 ? 1.88 : slx >= 95 ? 1.65 : slx >= 90 ? 1.28 : 1.04;
          const ss = Math.round(zx * sigma * Math.sqrt(45) * (avgDailyDemand / 100));
          return { serviceLevel: `${slx.toFixed(0)}%`, safetyStock: ss, holdingCost: Math.round(ss * 1240 * 0.18) };
        }),
        narrative: `Targeting ${sl}% fill rate with σ=${sigma}d lead-time variability requires ~${safetyStockUnits.toLocaleString()} units of safety stock (~${Math.round(investment / 1e6)}M₱ investment) across A-class SKUs. Curve flattens above 95% — investment doubles for marginal 2pp gain.`,
      };
    }
    case "po-frequency":
    default: {
      const n = inputs.ordersPerYear;
      const hold = inputs.holdingPct / 100;
      const annualDemand = 1_180_000;
      const eoqUnits = Math.round(annualDemand / n);
      const avgInvestment = Math.round((eoqUnits / 2) * 1_240);
      const holdingCost = Math.round(avgInvestment * hold);
      const orderingCost = n * 42_000;
      const total = holdingCost + orderingCost;
      return {
        outcomes: [
          { label: "EOQ per Order (units)", value: eoqUnits, format: "num" },
          { label: "Avg Inventory Investment", value: avgInvestment, format: "peso" },
          { label: "Annual Holding Cost", value: holdingCost, format: "peso" },
          { label: "Annual Total Cost", value: total, format: "peso" },
        ],
        series: Array.from({ length: 11 }).map((_, i) => {
          const freq = 4 + i * 5;
          const eo = Math.round(annualDemand / freq);
          const inv = (eo / 2) * 1240;
          const h = inv * hold;
          const o = freq * 42000;
          return { freq: `${freq}/yr`, holding: Math.round(h), ordering: o, total: Math.round(h + o) };
        }),
        narrative: `${n} POs/yr yields EOQ ${eoqUnits.toLocaleString()} units. Total annual cost ~${Math.round(total / 1e6)}M₱ (holding ${Math.round(holdingCost / 1e6)}M₱ + ordering ${Math.round(orderingCost / 1e6)}M₱). Sweet spot for most A-class SKUs is 10–14 orders/yr.`,
      };
    }
  }
}
